import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card } from "../../../../components/ui/Card";
import { showToast } from "../../../../utils/showToast";

/**
 * Landing page reached after the backend finishes the Jumpseller OAuth
 * exchange. The backend appends ?status=success|error and an optional
 * ?alias=storeName or ?error=code. We surface that to the user and bounce
 * them to /home/integrations so the new connection shows up in the grid.
 */
export default function JumpsellerCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const resolvedStatus = params.get("status") || "processing";
    const alias = params.get("alias");
    const errorCode = params.get("error");

    setStatus(resolvedStatus);

    if (resolvedStatus === "success") {
      showToast(
        alias
          ? t('Tienda Jumpseller "{{alias}}" vinculada correctamente', { alias })
          : t("Tienda Jumpseller vinculada correctamente"),
        true,
      );
    } else if (resolvedStatus === "error") {
      showToast(
        errorCode
          ? t("No se pudo vincular Jumpseller ({{error}})", { error: errorCode })
          : t("No se pudo vincular Jumpseller"),
        false,
      );
    }

    const timer = setTimeout(() => {
      navigate("/home/integrations", { replace: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [navigate, t]);

  return (
    <div className="flex justify-center items-center min-h-[60vh] px-4">
      <Card className="w-full max-w-[420px] text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-brand-500 animate-spin" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Procesando vinculación con Jumpseller…")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-status-success" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Tienda Jumpseller vinculada. Redirigiendo…")}</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-status-error" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("No se pudo completar la vinculación. Redirigiendo…")}</p>
          </>
        )}
      </Card>
    </div>
  );
}
